import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles, TrendingUp, Zap, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const founderTypes = [
  {
    icon: Sparkles,
    title: "First-Time Founders",
    stage: "Pre-seed to Seed",
    traits: [
      "Have an idea that will not let them sleep",
      "Want validation before going all-in",
      "Willing to learn and iterate quickly",
      "Care about building something real",
    ],
    ideal: "You are not looking for someone to tell you what to do. You are looking for a thought partner who can help you test your assumptions.",
  },
  {
    icon: TrendingUp,
    title: "Growth-Stage Founders",
    stage: "Seed to Series A",
    traits: [
      "Found initial traction",
      "Need to scale without burning out",
      "Want operational support",
      "Focused on sustainable growth",
    ],
    ideal: "You have proven something works. Now you need help building systems that scale without losing what made you successful.",
  },
];

const mindsetTraits = [
  "You carry real responsibility and feel it",
  "You prefer honest feedback over comfortable validation",
  "You are willing to hear no or not yet",
  "You value speed, but not at the cost of clarity",
  "You would rather build something small that works than something big that does not",
];

const whatTheyValue = [
  {
    icon: Target,
    title: "Outcomes over outputs",
    description: "They do not want deliverables for the sake of it. They want results that move the needle.",
  },
  {
    icon: Zap,
    title: "Speed with intention",
    description: "Fast execution is only valuable when you are moving in the right direction.",
  },
];

// Person standing with steps behind - showing journey/growth
const illustration = "/images/who-we-help.png";

export default function WhoWeHelpPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              Who We Help
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              We work with founders who carry real responsibility, and want a partner who understands the weight of that.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Types */}
      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-5">Two Types of Founders</h2>
            <p className="body-base text-muted-foreground">
              Whether you are just starting or already growing, we meet you where you are.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {founderTypes.map((type, i) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                <Card className="card-elevated h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="icon-container">
                        <type.icon className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-medium text-foreground">{type.title}</h3>
                        <p className="text-sm text-muted-foreground">{type.stage}</p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {type.traits.map((trait, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-foreground">{trait}</span>
                        </li>
                      ))}
                    </ul>

                    <p className="text-muted-foreground italic border-l-2 border-accent/30 pl-4">
                      {type.ideal}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mindset with Illustration */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="heading-section text-foreground mb-8">The Mindset That Fits</h2>
              
              <div className="bg-card border border-border/50 rounded-xl p-8">
                <p className="text-muted-foreground mb-6">
                  Stage matters less than mindset. If you resonate with these, we are probably a good fit:
                </p>
                <ul className="space-y-4">
                  {mindsetTraits.map((trait, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center text-accent text-sm font-medium flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-foreground">{trait}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex justify-center"
            >
              <div className="w-full max-w-sm">
                <img
                  src={illustration}
                  alt="Founder journey - step by step growth"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What They Value */}
      <section className="bg-background py-12 sm:py-16">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="heading-section text-foreground">What They Care About</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {whatTheyValue.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-flat flex gap-4"
              >
                <div className="icon-container flex-shrink-0">
                  <item.icon className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section mb-5">Sound Like You?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Tell us what you are working on. We will let you know if we can help.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
                <Link to="/contact">
                  Submit Your Wishlist
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
                <Link to="/not-for-everyone">Not Sure? Check Here</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
